import analytics from "./analytics";
import videojs from "video.js";
import "videojs-overlay";
import "./plugins/unmute";
import { jwtDecode } from "jwt-decode";
import { OVERLAYS } from "./plugins/overlay";

const playUrl = process.env.PLAY_URL;
const maxRetryDelay = 10;
const maxFunctionRetry = 5;
let player;
let tracks;
let context;
let errorCount = 0;
let functionErrorCount = 0;
let functionMaxRetryReached = false;
let refreshTimeout = null;

/**
 * VideoJS's XHR request intercepter. We use it to patch outgoing requests with the refreshed streaming tokens.
 * @param {*} options videojs xhr options
 * @returns patched options
 */
function globalXhrRequestHook(options) {
  const stream = getCurrentStream();
  const url = new URL(options.uri);
  const search = new URL(stream.source.src).search.substring(1);

  if (url.pathname.endsWith(".m3u8") || url.pathname.endsWith(".ts")) {
    url.search = `?${search}`;
  }

  options.uri = url.toString();
  return options;
}
videojs.Vhs.xhr.onRequest(globalXhrRequestHook);

/**
 * Function to calc a randomized but good time to refresh the streaming token
 * @param {number} expires ms until the token expires
 * @returns ms until nice time to refresh
 */
function niceRefreshTime(expires) {
  const now = Date.now();
  const halfDuration = expires * 0.5;
  const half = now + halfDuration;
  const last = now + expires * 0.9;
  return Math.floor(halfDuration + (last - half) * Math.random());
}

/**
 * Every now and then the play function errors out under high load.
 * Very very low fail rate, so let's just retry.
 */
async function handleFatalPlayFunctionError(jwt) {
  if (functionErrorCount >= maxFunctionRetry) {
    functionMaxRetryReached = true;
    throw new Error("ERR_MAX_FUNCTION_RETRIES_REACHED");
  }

  // wait a bit
  await new Promise((resolve) =>
    setTimeout(() => resolve(), functionErrorCount * 1000)
  );
  functionErrorCount++;
  // retry
  return fetchStreams(jwt);
}

/**
 * Fetches the streaming context (incl. token) and keeps it up2date.
 * @param {string} jwt initial or refresh token for play endpoint
 * @returns {void} when inital request is done
 */
async function fetchStreams(jwt) {
  const response = await window.fetch(playUrl, {
    method: "post",
    body: JSON.stringify({ jwt }),
  });

  if (response.status === 500) {
    return handleFatalPlayFunctionError(jwt);
  } else {
    functionErrorCount = 0;
  }

  context = await response.json();

  if (!response.ok) {
    throw context;
  }

  if (refreshTimeout) clearTimeout(refreshTimeout);
  refreshTimeout = setTimeout(
    () => fetchStreams(context.refresh),
    niceRefreshTime(context.expires * 1000)
  );
}

/**
 * Retrieve the current stream based on the chosen audio track
 * @returns {{ source: { src: string, type: string }, track: { enabled: boolean, id: string, kind: string, label: string }}}
 */
function getCurrentStream() {
  const track = Array.from(tracks).find((t) => t.enabled);
  return context.streams.find((stream) => stream.track.id === track.id);
}

/**
 * Override audio tracks explicitly and seal the AudioTrackList
 */
function setupAudioTracks() {
  Array.from(tracks).forEach((t) => tracks.removeTrack(t));
  context.streams.forEach((stream, idx) => {
    tracks.addTrack(new videojs.AudioTrack(stream.track));
  });
  // seal tracklist the sneaky way: prevent videojs to change it on metadata load
  tracks.addTrack = () => { };
  tracks.removeTrack = () => { };
}

/**
 * Track has been switched, start play it
 * */
function trackChanged() {
  const time = player.currentTime();
  const { source, track } = getCurrentStream();
  analytics.updateLanguage(track.label);
  player.src(source);
  player.trigger("hideNotice");
  player.play();
  // TODO: maybe don't seek in live mode?
  player.one("play", () => player.currentTime(time));
}

/**
 * Track has been switched, trigger appropriate analytic events
 * */
function trackTrackChange() {
  const { track } = getCurrentStream();
  analytics.sendClickEvent(track.label);
  analytics.sendViewEvent();
  player.off("play", analytics.sendPlayMediaEvent);
  player.one("play", analytics.sendPlayMediaEvent);
}

/**
 * Stream that played well seems to be not available anymore
 */
function playingStreamBrokeAway() {
  if (functionMaxRetryReached) {
    return player.error("ERR_MAX_FUNCTION_RETRIES_REACHED");
  }

  player.trigger("showNotice");
  // stream switched the server
  if (
    new URL(player.src()).hostname !==
    new URL(getCurrentStream().source.src).hostname
  ) {
    trackChanged(); // follow it
  }
}

/**
 * Verify or fail. On success kickstart player.
 */
async function init() {
  const jwtParam = new URL(window.location.href).searchParams.get("jwt");
  try {
    await fetchStreams(jwtParam);
  } catch (error) {
    player = videojs("player");
    player.error(error.code || error.message || error);
    return;
  }
  try {
    const jwt = jwtDecode(jwtParam);
    await analytics.init({
      id: jwt.stream,
      language: context.streams.find((stream) => stream.track.kind === "main")
        .track.label,
      event_state: jwt.aud === "liveplayer" ? "live" : "ondemand",
    });
  } catch (error) {
    console.log("Failed to init analytics", error);
  }

  player = videojs("player", {
    controls: true,
    muted: true,
    autoplay: "muted",
    html5: {
      vhs: { overrideNative: true },
      nativeAudioTracks: false,
      nativeVideoTracks: false,
    },
  });

  // Set unmute plugin
  player.unmute();
  // Set overlay plugin for streams that are not available yet
  player.overlay({
    overlays: [OVERLAYS.poster, OVERLAYS.beforeStartNotice],
  });
  player.trigger("showPoster");

  tracks = player.audioTracks();

  setupAudioTracks();
  tracks.addEventListener("change", trackChanged);

  player.on("play", () => {
    player.trigger("hideNotice");
    player.tech_.on("retryplaylist", playingStreamBrokeAway);
  });

  // we pass initial play-function and should be allowed to play video.
  // problems happening now are video-server-based (stream/vod not avaliable, ...)
  // so let's retry with exponential delay capped to {maxRetryDelay} seconds
  player.reloadSourceOnError({
    errorInterval: 1,
    getSource: function (reload) {
      if (functionMaxRetryReached) {
        return player.error("ERR_MAX_FUNCTION_RETRIES_REACHED");
      }

      player.error(null); // hide error as we display the overlay
      player.trigger("showNotice");
      const wait = Math.min(maxRetryDelay, 2 ** errorCount);
      setTimeout(
        () =>
          reload({
            src: getCurrentStream().source.src,
            type: getCurrentStream().source.sourceType,
          }),
        wait * 1000
      );
      errorCount++;
      console.log(`Error #${errorCount}, retrying in ${wait}s...`);
    },
  });

  player.one("playing", () => {
    player.trigger("hidePoster");
    // Set overlay plugin for streams that are/were available
    player.overlay({ overlays: [OVERLAYS.afterStopNotice] });

    analytics.sendPlayMediaEvent();
    tracks.addEventListener("change", trackTrackChange);
  });

  player.on("playing", () => {
    player.trigger("hideNotice");
    errorCount = 0;
  });

  player.on("ended", (event) => analytics.sendMediaEvent(event.type));

  tracks.trigger("change");

  analytics.sendViewEvent();

  window.p = player;
}

init();
